import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useSession } from '../store/session';
import { useTheme } from '../store/theme';
import { useConnectionState, useTicks } from '../data/DataProvider';
import { useContractMeta, useMarketStatus, useWatchlistSync } from '../data/hooks';
import { SearchOverlay } from './SearchOverlay';
import { Change, LiveNum, MarketClock } from './pro';
import {
  IconBell,
  IconBriefcase,
  IconHome,
  IconMoon,
  IconReport,
  IconSearch,
  IconStar,
  IconSun,
  IconUser,
  IconWallet,
} from './icons';

const NAV = [
  { to: '/explore', label: 'Explore', icon: IconHome, end: true },
  { to: '/watchlist', label: 'Watchlist', icon: IconStar },
  { to: '/portfolio', label: 'Portfolio', icon: IconBriefcase },
  { to: '/orders', label: 'Orders', icon: IconReport },
  { to: '/wallet', label: 'Funds', icon: IconWallet },
] as const;

/**
 * The benchmarks a trader keeps in the corner of their eye. It runs under the
 * nav on every screen rather than only on Explore: a dealing screen without a
 * reference index in view is a screen that makes you navigate to find out
 * whether it is the stock moving or the market.
 */
const TAPE = ['NIFTY 50', 'NIFTY BANK', 'SENSEX', 'NIFTY FIN SERVICE', 'INDIA VIX'];

function MarketTape() {
  const ticks = useTicks(TAPE);
  const meta = useContractMeta(TAPE);
  const known = TAPE.filter((s) => ticks[s] || meta[s]);

  return (
    <div className="tape-strip">
      <div className="tape-scroll">
        {known.length === 0
          ? TAPE.map((s) => (
              <span className="tape-item" key={s}>
                <span className="tape-name">{s}</span>
                <span className="num muted">—</span>
              </span>
            ))
          : known.map((s) => (
              <Link
                to={`/stocks/${encodeURIComponent(s)}`}
                className="tape-item"
                key={s}
                title={meta[s]?.name ?? s}
              >
                <span className="tape-name">{meta[s]?.name ?? s}</span>
                <LiveNum value={ticks[s]?.ltp} className="semibold" />
                <Change tick={ticks[s]} className="t-xs" />
              </Link>
            ))}
      </div>
      <div className="tape-clock">
        <FeedChip />
        <MarketClock />
      </div>
    </div>
  );
}

/** Applies the persisted theme to <html>, which is what the stylesheet keys on. */
export function ThemeSync() {
  const theme = useTheme((s) => s.theme);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  return null;
}

/**
 * The feed's honesty indicator. It never claims "live" on the strength of the
 * app being open — the state comes from the gateway's own feed frames, so a
 * dead upstream reads as reconnecting even while the socket is healthy.
 */
function FeedChip() {
  const state = useConnectionState();
  const market = useMarketStatus();

  if (state === 'live') {
    return (
      <span className="chip chip-up" title="Live market data">
        <span className="dot dot-live" />
        {market?.label ?? 'LIVE'}
      </span>
    );
  }
  if (state === 'closed') {
    return (
      <span className="chip" title="Market is closed — showing last traded prices">
        {market?.label ?? 'CLOSED'}
      </span>
    );
  }
  const label =
    state === 'offline' ? 'Offline' : state === 'stale' ? 'Prices delayed' : 'Reconnecting…';
  return (
    <span className="chip chip-warn" title="Prices are not live right now">
      <span className="dot" />
      {label}
    </span>
  );
}

function NotificationsBell() {
  const token = useSession((s) => s.token);
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['notifications', 'badge'],
    queryFn: () => api.getNotifications(),
    enabled: !!token,
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  const unread = data?.unread ?? 0;
  return (
    <button
      className="icon-btn"
      onClick={() => navigate('/notifications')}
      aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
      style={{ position: 'relative' }}
    >
      <IconBell />
      {unread > 0 ? (
        <span
          className="num"
          style={{
            position: 'absolute',
            top: 3,
            right: 3,
            minWidth: 15,
            height: 15,
            padding: '0 4px',
            borderRadius: 8,
            background: 'var(--down)',
            color: '#fff',
            fontSize: 9,
            fontWeight: 700,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </button>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const theme = useTheme((s) => s.theme);
  const toggleTheme = useTheme((s) => s.toggle);
  const token = useSession((s) => s.token);

  // The account's watchlist follows them to this browser, once per sign-in.
  useWatchlistSync();

  // ⌘K / Ctrl-K from anywhere. Ignored while typing, so "k" in the order
  // ticket's quantity field does not hijack the keystroke.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (e.key === '/' && !typing) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <header className="nav">
        <div className="nav-inner">
          <NavLink to="/" className="brand" aria-label="Dabba home">
            <span className="brand-mark">D</span>
            <span>dabba</span>
          </NavLink>

          <button className="nav-search" onClick={() => setSearchOpen(true)}>
            <IconSearch size={17} />
            <span className="t-md">Search stocks, F&O…</span>
            <kbd className="kbd">⌘K</kbd>
          </button>

          <nav className="nav-links">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={'end' in n ? n.end : false}
                className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              >
                {n.label}
              </NavLink>
            ))}
            {token ? <NotificationsBell /> : null}
            <button
              className="icon-btn"
              onClick={toggleTheme}
              aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            >
              {theme === 'light' ? <IconMoon /> : <IconSun />}
            </button>
            <NavLink to="/profile" className="icon-btn" aria-label="Your account">
              <IconUser />
            </NavLink>
          </nav>
        </div>
      </header>

      <MarketTape />

      <main>{children}</main>

      <nav className="tabbar" aria-label="Primary">
        {NAV.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={'end' in n ? n.end : false}
              className={({ isActive }) => `tabbar-item${isActive ? ' active' : ''}`}
            >
              <Icon size={20} />
              {n.label}
            </NavLink>
          );
        })}
      </nav>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
